import React, { useState, useEffect } from 'react';
import { Card, List, Button, Modal, Form, Input, message } from 'antd';
import { LockOutlined, PhoneOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import { getAccountInfo, phoneVerifyCode, updatePhone } from '../../api/user';
import { ApiResponse } from '../../types';
import { AccountSetting } from '../../types/user';
import { AxiosResponse } from 'axios';
import { sendSMS } from '../../api/common';
import type { FormProps } from 'antd';
import { useTranslation } from 'react-i18next';

const AccountSecuritySettings = () => {
  const [securityInfo, setSecurityInfo]     = useState<AccountSetting|null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [modalType, setModalType]           = useState<string>('');
  const [phoneChangeStep, setPhoneChangeStep] = useState<number>(0);
  const [countdown, setCountdown]             = useState<number>(0);

  const [form] = Form.useForm();
  const { t } = useTranslation();

  useEffect(() => {
    fetchAccountInfo();
  }, []);

  useEffect(() => {
    let timer: number;
    if (countdown > 0) {
      timer = window.setInterval(() => {
        setCountdown((prevCountdown) => prevCountdown - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  const fetchAccountInfo = () => {
    getAccountInfo().then((res: AxiosResponse<ApiResponse<AccountSetting>>) => {
      // console.log(res.data)
      setSecurityInfo(res.data.data)
    })
  } 
   

  const showModal = (type: string) => {
    setModalType(type);
    setIsModalVisible(true);
    if (type === 'phone') {
      setPhoneChangeStep(0);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    if (modalType === 'phone') {
      setPhoneChangeStep(0);
    }
  };

  const onFinish: FormProps['onFinish'] = (values) => {
    console.log('Received values:', values);
    if (modalType === 'phone') {
      if (phoneChangeStep === 0) {
        // 验证当前手机号的验证码
        console.log('Verifying current phone:', values.currentPhoneVerificationCode);
        if (!securityInfo) {
          message.error("手机号为空")
          return
        }
        phoneVerifyCode({phone: securityInfo.phone, code: values.currentPhoneVerificationCode}).then((res: AxiosResponse<ApiResponse>) => {

          // console.log("res: ", res.status, res.data)
          if (res.data.status === 200) {
            setPhoneChangeStep(1);
            form.resetFields();
          } else {
            message.error(t('message.error.smscode'))
          }
        })

        
      } else if (phoneChangeStep === 1) {
        // 验证新手机号的验证码
        console.log('Updating to new phone:', values.newPhone, 'with code:', values.newPhoneVerificationCode);

        updatePhone({phone: values.newPhone, 'code': values.newPhoneVerificationCode}).then((res: AxiosResponse<ApiResponse>) => {
          if (res.data.status === 200) {
            message.success('手机号更新成功');
            setIsModalVisible(false);
            setPhoneChangeStep(0);
            fetchAccountInfo();
          }
        })
        
        // fetchSecurityInfo();
      }
    } else {
      message.success('设置已更新');
      setIsModalVisible(false);
    }
    // 这里应该调用API来更新信息
  };

const sendVerificationCode = (type: string) => {
    const phoneNumber = type === 'current' ? securityInfo?.phone : form.getFieldValue('newPhone');
    if (phoneNumber && phoneNumber.length < 15) {
      if (type === 'new') {
        var newPhone = form.getFieldValue('newPhone')
        if (!securityInfo) {
          message.error("旧手机号为空")
          return
        }
        if (newPhone === securityInfo.phone) {
          message.warning("新手机号不能与之前相同")
          return
        }
      }
      setCountdown(60);
      
      sendSMS({phone: phoneNumber, type: 1}).then((res: AxiosResponse<ApiResponse>) => {
        if (res.data.status === 200) {
          message.success(`验证码已发送到${type === 'current' ? '当前' : '新'}手机号`);
        }
      })
    } else {
      message.error('请输入有效的手机号');
    }
};

  const renderModalContent = () => {
    switch (modalType) {
      case 'password':
        return (
          <>
            <Form.Item
              name="oldPassword"
              rules={[{ required: true, message: '请输入当前密码' }]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="当前密码" />
            </Form.Item>
            <Form.Item
              name="newPassword"
              rules={[
                { required: true, message: '请输入新密码' },
                { min: 8, message: '密码长度至少为8位' }
              ]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="新密码" />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: '请确认新密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="确认新密码" />
            </Form.Item>
          </>
        );
      case 'phone':
        if (phoneChangeStep === 0) {
          return (
            <>
              <Form.Item label={t('setting.modal.label1')}>
                <Input disabled value={securityInfo?.phone} />
              </Form.Item>
              <Form.Item
                name="currentPhoneVerificationCode"
                label={t('setting.modal.label3')}
                rules={[{ required: true, message: t('setting.modal.smscode_placehoder') }]}
              >
                <Input 
                  placeholder={t('setting.modal.smscode_placehoder')}
                  addonAfter={
                    <Button 
                      type="link" 
                      onClick={() => sendVerificationCode('current')}
                      disabled={countdown > 0}
                      size="small"
                    >
                      {countdown > 0 ? `${countdown}s` : t('setting.modal.send_smscode')}
                     </Button>
                  }
                />
              </Form.Item>
            </>
          );
        } else {
          return (
            <>
              <Form.Item
                name="newPhone"
                label={t('setting.modal.label2')}
                rules={[
                  { required: true, message: t('setting.modal.phone_check_rule1') },
                  { pattern: /^1\d{10}$/, message: t('setting.modal.phone_check_rule2') }
                ]}
              >
                <Input prefix={<PhoneOutlined className={styles.inputIcon} />} placeholder={t('setting.modal.phone_check_rule1')} />
              </Form.Item>
              <Form.Item
                name="newPhoneVerificationCode"
                label={t('setting.modal.label3')}
                rules={[{ required: true, message: t('setting.modal.smscode_placehoder') }]}
              >
                <Input 
                  prefix={<LockOutlined className={styles.inputIcon} />}
                  placeholder={t('setting.modal.smscode_placehoder')}
                  addonAfter={
                    <Button 
                      type="link" 
                      onClick={() => sendVerificationCode('new')}
                      disabled={countdown > 0}
                      size="small"
                    >
                      {countdown > 0 ? `${countdown}s` : t('setting.modal.send_smscode')}
                     </Button>
                  }
                />
              </Form.Item>
            </>
          );
        }
      // 可以添加更多类型的表单内容
      default:
        return null;
    }
  };

  const securityItems = [
    { title: t('setting.phone_number'), value: securityInfo?.phone, action: t('setting.modify'), onClick: () => showModal('phone') },

  ];
 
  return (
    <div className={styles.accountSecuritySettings}>
      
      <Card>
        <List
          itemLayout="horizontal"
          dataSource={securityItems}
          renderItem={(item) => (
            <List.Item actions={item.action ? [<a key="action" onClick={item.onClick}>{item.action}</a>] : undefined}>
              <List.Item.Meta
                title={item.title}
                description={item.value}
              />
            </List.Item>
          )}
        />
      </Card>
      <Modal
        title={modalType === 'phone' ? (phoneChangeStep === 0 ? t('setting.modal.title1') : t('setting.modal.title2')) : t('setting.modal.title3')}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
      >
        <Form form={form} onFinish={onFinish} layout="vertical">
          {renderModalContent()}
          <Form.Item>
            <Button type="primary" htmlType="submit" className={styles.submitButton}>
              {modalType === 'phone' && phoneChangeStep === 0 ? t('setting.modal.submit2') : t('setting.modal.submit1')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AccountSecuritySettings;